import { Color } from "@repo/app-types/constants/colors";
import type { IntegrationProvider } from "@repo/app-types/entities/integration";
import {
  getRecordingImportErrorLabel,
  RECORDING_IMPORT_ACTION_FILTER_CAP,
  RECORDING_IMPORT_ACTION_MAX_IDS,
  RECORDING_IMPORT_CUSTOMER_STATUS,
  recordingImportActionsForStatus,
  type RecordingImportAction,
  type RecordingImportActionSelection,
  type RecordingImportId,
  type RecordingImportRow,
} from "@repo/app-types/entities/recordingImport";
import { RECORDING_INTAKE_NO_ERROR_CODE_KEY } from "@repo/app-types/entities/recordingIntake";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { Pill } from "@repo/ui/Pills/pill";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Dialog, type SortDescriptor } from "react-aria-components";
import {
  useRecordingImportCountsByCodeQuery,
  useRecordingImportsInfiniteQuery,
  useRecordingImportStatusCountsQuery,
  useReprocessRecordingImportsMutation,
  useSkipRecordingImportsMutation,
} from "../../../api/entities/recordingImport.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { resolveRecordingImportProviderColumns } from "./recordingImportProviderColumns.js";

/**
 * A pending action and what it targets: explicit rows (one row's button, or
 * the checked rows), or every row matching a chip (the "all matching" buttons;
 * the server changes at most {@link RECORDING_IMPORT_ACTION_FILTER_CAP} per
 * click). The filter form snapshots the chip the count was read for, so the
 * action sends exactly what the confirm quoted even if the view changes
 * underneath it.
 */
interface PendingAction {
  action: RecordingImportAction;
  target:
    | { kind: "ids"; ids: readonly RecordingImportId[] }
    | {
        kind: "filter";
        errorCode: string | null;
        matchingCount: number;
      };
}

// A customer sees only failed rows, the ones waiting on a fix: pending rows
// resolve on their own, imported ones are already recordings in the app, and
// skipped ones (the pipeline's skips and their own dismissals) are not shown.
// The actions are the ones a failed row takes.
const status = RECORDING_IMPORT_CUSTOMER_STATUS;
const actions = recordingImportActionsForStatus(status);

function formatDateTime(iso: string): string {
  return DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED);
}

function pluralize(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? "" : "s"}`;
}

const receivedAtColumn: EntityTableColumnDefinition<RecordingImportRow> = {
  id: "intaken_at",
  header: "Received at",
  isRowHeader: true,
  allowsSorting: true,
  getTextValue: (row) => row.intakenAt,
  renderCell: (row) => (
    <span className="whitespace-nowrap text-foreground">
      {formatDateTime(row.intakenAt)}
    </span>
  ),
};

/**
 * The row's error: the readable label on the pill, and what an operator greps
 * for (the raw code, the underlying message) in the tooltip. Rendered the same
 * way on the HQ `/recording-imports` page, deliberately duplicated rather than
 * shared through `packages/ui`.
 */
const ErrorPill: FC<{
  errorCode: string | null;
  errorMessage: string | null;
}> = (props) => {
  const { errorCode, errorMessage } = props;
  const label = getRecordingImportErrorLabel(errorCode);
  if (!label) {
    return <span className="text-muted-foreground">—</span>;
  }
  const pill = <Pill label={label} size={Size.SMALL} color={Color.Red} />;
  // An unknown code is its own label, so repeating it would say nothing.
  const showCode = label !== errorCode;
  if (!showCode && !errorMessage) {
    return pill;
  }
  return (
    <TooltipTrigger>
      <TooltipHoverTarget shape="rounded">{pill}</TooltipHoverTarget>
      <SimpleTooltip placement="top">
        <div className="space-y-1">
          {showCode ? (
            <div className="font-mono text-xs text-gray-300">{errorCode}</div>
          ) : null}
          {errorMessage ? <div>{errorMessage}</div> : null}
        </div>
      </SimpleTooltip>
    </TooltipTrigger>
  );
};

const errorColumn: EntityTableColumnDefinition<RecordingImportRow> = {
  id: "error",
  header: "Error",
  getTextValue: (row) => row.errorCode ?? "",
  renderCell: (row) => (
    <ErrorPill errorCode={row.errorCode} errorMessage={row.errorMessage} />
  ),
};

/**
 * The leading selection column: a per-row checkbox plus a "select all" header
 * over the currently-loaded rows (indeterminate when only some are selected).
 */
function buildSelectionColumn(
  rows: readonly RecordingImportRow[],
  selectedIds: ReadonlySet<RecordingImportId>,
  describeRow: (row: RecordingImportRow) => string,
  onToggleRow: (id: RecordingImportId, shouldSelect: boolean) => void,
  onToggleAll: (shouldSelect: boolean) => void,
): EntityTableColumnDefinition<RecordingImportRow> {
  const allSelected =
    rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  const someSelected = rows.some((row) => selectedIds.has(row.id));
  return {
    id: "select",
    className: "w-10",
    header: (
      <Checkbox
        isChecked={allSelected}
        isIndeterminate={!allSelected && someSelected}
        onChange={onToggleAll}
        accessibleLabel="Select all loaded rows"
      />
    ),
    renderCell: (row) => (
      <Checkbox
        isChecked={selectedIds.has(row.id)}
        onChange={(shouldSelect) => onToggleRow(row.id, shouldSelect)}
        accessibleLabel={`Select ${describeRow(row)}`}
      />
    ),
  };
}

const actionLabels: Record<RecordingImportAction, string> = {
  reprocess: "Reprocess",
  skip: "Skip",
};

/** The trailing per-row actions, each behind the confirm. */
function buildActionsColumn(
  actions: readonly RecordingImportAction[],
  onAction: (action: RecordingImportAction, id: RecordingImportId) => void,
): EntityTableColumnDefinition<RecordingImportRow> {
  return {
    id: "actions",
    header: "Actions",
    renderCell: (row) => (
      <div className="flex justify-end gap-2 whitespace-nowrap">
        {actions.map((action) => (
          <Button
            key={action}
            size={Size.SMALL}
            variant={ButtonVariant.Secondary}
            onClick={() => onAction(action, row.id)}
          >
            {actionLabels[action]}
          </Button>
        ))}
      </div>
    ),
  };
}

interface BulkActionBarProps {
  count: number;
  actions: readonly RecordingImportAction[];
  isBusy: boolean;
  onAction: (action: RecordingImportAction) => void;
  onClear: () => void;
}

/** The bulk action bar shown when at least one row is selected. */
const BulkActionBar: FC<BulkActionBarProps> = (props) => {
  const { count, actions, isBusy, onAction, onClear } = props;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border-light bg-muted px-3 py-2">
      <span className="text-sm font-medium text-foreground">
        {count.toLocaleString()} selected
      </span>
      <div className="flex items-center gap-2">
        {actions.map((action) => (
          <Button
            key={action}
            size={Size.SMALL}
            variant={ButtonVariant.Secondary}
            isDisabled={isBusy}
            onClick={() => onAction(action)}
          >
            {actionLabels[action]}
          </Button>
        ))}
        <Button
          size={Size.SMALL}
          variant={ButtonVariant.Tertiary}
          onClick={onClear}
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

export interface RecordingImportsTabProps {
  /** The integration whose ledger rows to show; the API scopes everything to it. */
  integrationId: string;
  /** Picks the columns that render the rows' provider-shaped `metadata`. */
  provider: IntegrationProvider;
  /** While Alpharun has paused the integration the API refuses actions; hide them. */
  isPaused: boolean;
  /** The lead-in sentence above the table, in the provider's own words. */
  description: string;
}

/**
 * The generic **Failed imports** tab: one integration's failed
 * `recording_import` rows, with error-code chips and infinite scroll.
 * Teammates who can update integrations get reprocess and skip, per row, over
 * the checked rows, or over everything matching the current chip.
 * Provider-agnostic: the provider only picks the columns that render
 * `metadata`. See .agents/wiki/recording-import.md.
 */
export const RecordingImportsTab: FC<RecordingImportsTabProps> = (props) => {
  const { integrationId, provider, isPaused, description } = props;
  const dispatch = useAppDispatch();
  const ability = useTeammateAuthorization();
  const canUpdate = ability.can(
    PermissionActions.UPDATE,
    PermissionSubjects.INTEGRATIONS,
  );
  // Failed rows always have actions, so this is the only gate on them.
  const hasActions = canUpdate && !isPaused;
  // A module-level constant per provider family, so it is referentially
  // stable as is.
  const providerColumns = resolveRecordingImportProviderColumns(provider);

  // Only "Received at" sorts; clicking its header flips the direction.
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "intaken_at",
    direction: "descending",
  });
  const order = sortDescriptor.direction === "ascending" ? "asc" : "desc";

  // The selected chip: null = every code (the "All" chip), the `__none__`
  // sentinel = rows carrying no code, else a literal code. Part of the query
  // arg, so picking a chip narrows server-side and starts a fresh page 1.
  const [codeFilter, setCodeFilter] = useState<string | null>(null);

  // `currentData` rather than `data`: switching chip or sort changes the cache
  // key, and `data` would keep showing the previous key's rows until the new
  // page resolves.
  const {
    currentData: data,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRecordingImportsInfiniteQuery({
    integrationId,
    status,
    order,
    errorCode: codeFilter,
  });

  const { data: statusCounts } = useRecordingImportStatusCountsQuery({
    integrationId,
  });

  // The chips ride a separate read never narrowed by the selected code, so
  // picking a chip doesn't refetch and blank them.
  const { currentData: counts } = useRecordingImportCountsByCodeQuery({
    integrationId,
    status,
  });
  const countsByCode = counts?.countsByCode ?? null;

  const [reprocess, { isLoading: isReprocessing }] =
    useReprocessRecordingImportsMutation();
  const [skip, { isLoading: isSkipping }] = useSkipRecordingImportsMutation();
  const isBusy = isReprocessing || isSkipping;

  const [selectedIds, setSelectedIds] = useState<
    ReadonlySet<RecordingImportId>
  >(new Set());
  const [pending, setPending] = useState<PendingAction | null>(null);

  const rows = useMemo(
    () => data?.pages.flatMap((entry) => entry.rows) ?? [],
    [data],
  );

  // "Select all" only covers the rows one ids action accepts.
  const selectableRows = useMemo(
    () => rows.slice(0, RECORDING_IMPORT_ACTION_MAX_IDS),
    [rows],
  );

  // Prune the selection to the rows currently shown, so ids dropped by a
  // refetch (after a reprocess sends them back to pending, say) don't linger.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const visible = new Set(rows.map((row) => row.id));
      const next = new Set<RecordingImportId>();
      for (const id of prev) {
        if (visible.has(id)) {
          next.add(id);
        }
      }
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  // The "All" chip's total, and the count the "all matching" buttons quote.
  const allCodesTotal = useMemo(
    () => (countsByCode ?? []).reduce((sum, entry) => sum + entry.count, 0),
    [countsByCode],
  );
  const totalMatching = useMemo(() => {
    if (!countsByCode) {
      // Before the chip counts load, the failed count is the best one
      // available.
      return statusCounts?.[status] ?? 0;
    }
    if (codeFilter === null) {
      return allCodesTotal;
    }
    return (
      countsByCode.find(
        (entry) =>
          (entry.code ?? RECORDING_INTAKE_NO_ERROR_CODE_KEY) === codeFilter,
      )?.count ?? 0
    );
  }, [countsByCode, statusCounts, codeFilter, allCodesTotal]);

  // Drop a chip filter whose chip has gone — acting on the last row of a code
  // removes it from the counts, which would otherwise leave an empty table
  // filtered by an invisible chip. Guarded on the counts having loaded so the
  // filter isn't cleared during the fetch it just triggered.
  useEffect(() => {
    if (!codeFilter || !countsByCode) {
      return;
    }
    const isLive = countsByCode.some(
      (entry) =>
        (entry.code ?? RECORDING_INTAKE_NO_ERROR_CODE_KEY) === codeFilter,
    );
    if (!isLive) {
      setCodeFilter(null);
    }
  }, [codeFilter, countsByCode]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const toggleRow = useCallback(
    (id: RecordingImportId, shouldSelect: boolean) => {
      setSelectedIds((prev) => {
        // Never let the selection grow past what one ids action accepts, so the
        // payload can never 400 against the server.
        if (
          shouldSelect &&
          !prev.has(id) &&
          prev.size >= RECORDING_IMPORT_ACTION_MAX_IDS
        ) {
          return prev;
        }
        const next = new Set(prev);
        if (shouldSelect) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );

  const toggleAll = useCallback(
    (shouldSelect: boolean) => {
      setSelectedIds(
        shouldSelect ? new Set(selectableRows.map((row) => row.id)) : new Set(),
      );
    },
    [selectableRows],
  );

  const requestRowAction = useCallback(
    (action: RecordingImportAction, id: RecordingImportId) =>
      setPending({ action, target: { kind: "ids", ids: [id] } }),
    [],
  );

  const columns = useMemo(() => {
    const base = [receivedAtColumn, ...providerColumns.columns, errorColumn];
    return hasActions
      ? [
          buildSelectionColumn(
            selectableRows,
            selectedIds,
            providerColumns.describeRow,
            toggleRow,
            toggleAll,
          ),
          ...base,
          buildActionsColumn(actions, requestRowAction),
        ]
      : base;
  }, [
    providerColumns,
    hasActions,
    selectableRows,
    selectedIds,
    toggleRow,
    toggleAll,
    requestRowAction,
  ]);

  const selectedIdList = useMemo(() => [...selectedIds], [selectedIds]);
  const selectedCount = selectedIdList.length;

  const runPending = useCallback(async () => {
    if (!pending) {
      return;
    }
    const { action, target } = pending;
    setPending(null);
    const selection: RecordingImportActionSelection =
      target.kind === "ids"
        ? { ids: [...target.ids] }
        : {
            filter: {
              status,
              ...(target.errorCode ? { errorCode: target.errorCode } : {}),
            },
          };
    try {
      const run = action === "reprocess" ? reprocess : skip;
      await run({ integrationId, selection }).unwrap();
      clearSelection();
    } catch (error) {
      // The mutation error isn't rendered anywhere, so without this the dialog
      // just closes and the rows silently stay put. Keep the selection so the
      // user can retry.
      showNotification(dispatch, {
        id: `recording-import-${action}-error-${Date.now()}`,
        type: NotificationType.FAILURE,
        primaryMessage:
          action === "reprocess"
            ? "Couldn't reprocess the selected imports"
            : "Couldn't skip the selected imports",
        secondaryMessage: getApiErrorMessage(error, "Please try again."),
      });
    }
  }, [pending, reprocess, skip, integrationId, clearSelection, dispatch]);

  const pendingCount =
    pending?.target.kind === "ids"
      ? pending.target.ids.length
      : (pending?.target.matchingCount ?? 0);
  const isPendingFilter = pending?.target.kind === "filter";
  const isSkip = pending?.action === "skip";

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
        {description}
      </p>

      {isPaused ? (
        <div className="max-w-3xl">
          <InfoBanner
            variant="CAUTION"
            title="Importing is currently paused by Alpharun"
            description="Reprocess and skip are available again once it resumes."
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {hasActions && totalMatching > 0 ? (
          <div className="flex items-center gap-2">
            {actions.map((action) => (
              <Button
                key={action}
                size={Size.SMALL}
                variant={ButtonVariant.Secondary}
                isDisabled={isBusy}
                onClick={() =>
                  setPending({
                    action,
                    target: {
                      kind: "filter",
                      errorCode: codeFilter,
                      matchingCount: totalMatching,
                    },
                  })
                }
              >
                {`${actionLabels[action]} all matching (${totalMatching.toLocaleString()})`}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      {countsByCode && countsByCode.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <Pill
            label={`All (${allCodesTotal.toLocaleString()})`}
            size={Size.SMALL}
            color={codeFilter ? Color.Gray : Color.DarkBlue}
            onClick={() => setCodeFilter(null)}
          />
          {countsByCode.map((entry) => {
            const key = entry.code ?? RECORDING_INTAKE_NO_ERROR_CODE_KEY;
            const isActive = codeFilter === key;
            const label =
              getRecordingImportErrorLabel(entry.code) ?? "No error code";
            return (
              <Pill
                key={key}
                label={`${label} (${entry.count.toLocaleString()})`}
                size={Size.SMALL}
                color={isActive ? Color.Red : Color.Gray}
                onClick={() => setCodeFilter(isActive ? null : key)}
              />
            );
          })}
        </div>
      ) : null}

      {hasActions && selectedCount > 0 ? (
        <BulkActionBar
          count={selectedCount}
          actions={actions}
          isBusy={isBusy}
          onAction={(action) =>
            setPending({
              action,
              target: { kind: "ids", ids: selectedIdList },
            })
          }
          onClear={clearSelection}
        />
      ) : null}

      {/*
        EntityTable has no scroll container of its own; a bounded, scrollable box
        gives its sticky header a range to stick against and keeps a wide table
        from scrolling the whole page.
      */}
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-border-light">
        <EntityTable
          aria-label="Failed imports"
          columns={columns}
          entities={rows}
          // `isLoading` only covers the hook's first fetch; after a chip or
          // sort change `currentData` is empty while the new page
          // loads, which must read as loading, not as an empty list.
          isLoading={isLoading || (isFetching && data === undefined)}
          isError={isError}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
          onLoadMore={
            hasNextPage && !isFetchingNextPage
              ? () => void fetchNextPage()
              : undefined
          }
          emptyMessage="No failed imports"
        />
      </div>

      <CenteredModal
        isOpen={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
          }
        }}
        isDismissable
      >
        <Dialog className="w-screen max-w-md space-y-4 p-4">
          <ModalHeader isCloseVisible>
            {isSkip ? "Skip imports" : "Reprocess imports"}
          </ModalHeader>
          <p className="text-sm text-muted-foreground">
            {isSkip
              ? `Dismiss ${pluralize(pendingCount, "failed import")}. They'll no longer be shown here.`
              : `Try ${pluralize(pendingCount, "import")} again. They'll leave this list while they run.`}
            {isPendingFilter &&
            pendingCount > RECORDING_IMPORT_ACTION_FILTER_CAP
              ? ` Up to ${RECORDING_IMPORT_ACTION_FILTER_CAP.toLocaleString()} are changed per click; repeat for the rest.`
              : ""}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size={Size.MEDIUM}
              variant={ButtonVariant.Secondary}
              slot="close"
            >
              Cancel
            </Button>
            <Button
              size={Size.MEDIUM}
              variant={ButtonVariant.Primary}
              onClick={() => void runPending()}
            >
              {isSkip ? "Skip" : "Reprocess"}
            </Button>
          </div>
        </Dialog>
      </CenteredModal>
    </div>
  );
};
